import React, { useState, useEffect, useRef } from "react";
import Sidebar from "../../../../components/sidebar";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button } from "react-bootstrap";
import "../style.scss";
import ClockYellowIcon from "../../../../icons/ClockYellowIcon";
import SearchIcon from "../../../../icons/SearchIcon";
import { useDispatch } from "react-redux";
import { all_routes } from "../../../router/all_routes";
import TravelActivityIcon from "../../../../icons/TravelActivityIcon";
import ArrowUpWhiteIcon from "../../../../icons/ArrowUpWhiteIcon";
import UploadIcon from "../../../../icons/UploadIcon";
import FileUploadWhiteIcon from "../../../../icons/FileUploadWhiteIcon";
import MileageEntryModal from "../../../../core/components/modals/mileageEntryModal";
import SpouseLoggedHourModal from "../../../../core/components/modals/spouseLoggedHourModal";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { getPropertyListForFilter } from "../../../../core/data/redux/property/_propertyAction";
import { activityList, mainCategoryList, subActivityList } from "../../../../services/category.service";
import { MileageEntry } from "../../../../constants/types";
import { addLogHour, getLogHourDetails, getRemainingLogHour, getSpouseLogHours, updateLogHour } from "../../../../core/data/redux/logHour/_logHourAction";
import DeleteModal from "../../../../components/deleteModal";
import GreenFileIcon from "../../../../icons/GreenFileIcon";
import { MAX_FILE_SIZE, MAX_FILES, MAX_TOTAL_SIZE } from "../../../../constants/common";
import { formatDateStringType } from "../../../../constants/date";


const validationSchema = Yup.object({
  description: Yup.string().required("Task description is required"),

  hours: Yup.number()
    .typeError("Hours must be a number")
    .min(0, "Hours cannot be less than 0")
    .max(24, "Hours cannot be more than 24")
    .test(
      "hours-or-minutes",
      "Either hours or minutes must be greater than 0",
      function (value) {
        const { minutes } = this.parent; // access sibling field
        return (value ?? 0) > 0 || (minutes ?? 0) > 0;
      }
    ),

  minutes: Yup.number()
    .typeError("Minutes must be a number")
    .min(0, "Minutes cannot be less than 0")
    .max(60, "Minutes cannot be more than 60")
    .test(
      "hours-or-minutes",
      "Either hours or minutes must be greater than 0",
      function (value) {
        const { hours } = this.parent; // access sibling field
        return (value ?? 0) > 0 || (hours ?? 0) > 0;
      }
    ),

  property: Yup.string().required("Please select a property"),
  date: Yup.string().required("Please pick a date"),
  main_category: Yup.string().required("Please choose activity category"),

  sub_activity: Yup.string().when("main_category", {
    is: (val: string) => !!val,
    then: (schema) => schema.required("Please choose sub activity"),
    otherwise: (schema) => schema.notRequired(),
  }),

  activity: Yup.string().when(["main_category", "sub_activity"], {
    is: (cat: string, sub: string) => !!cat && !!sub,
    then: (schema) => schema.required("Please choose activity"),
    otherwise: (schema) => schema.notRequired(),
  }),

  is_travel: Yup.boolean(),

  mileageData: Yup.object()
    .nullable()
    .when("is_travel", {
      is: true,
      then: (schema) =>
        schema.shape({
          startingLocation: Yup.string().required("Starting location is required"),
          destinationLocation: Yup.string().required("Destination location is required"),
          route_trip: Yup.boolean(),
          miles: Yup.string().required("Miles are required"), // Adjust type if miles is a number
          isRecurring: Yup.boolean().required("Recurring status is required"),
          days: Yup.string().when("isRecurring", {
            is: true,
            then: (schema) => schema.required("Days are required for recurring trips"),
            otherwise: (schema) => schema.notRequired(),
          }),
          photos: Yup.array().of(Yup.mixed()).nullable(),
        }).required("Mileage entry is required when travel is involved"),
      otherwise: (schema) => schema.notRequired(),
    }),

  notes: Yup.string().max(500, "Maximum 500 characters"),
  receipt_images: Yup.mixed().nullable(),
  activity_description: Yup.string().max(500, "Maximum 500 characters").nullable(),
});

const initialValues = {
  description: "",
  hours: 0,
  minutes: 0,
  property: "",
  date: "",
  main_category: "",
  sub_activity: "",
  activity: "",
  notes: "",
  is_travel: false,
  mileageData: null,
  receipt_images: null,
  activity_description: "",
}


const SpouseLogHoursAdd: React.FC = () => {
  const { isSidebarOpen } = useOutletContext<{ isSidebarOpen: boolean; onToggleSidebar: () => void }>();
  const { id } = useParams<{ id: string }>(); // id in route => edit mode
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const routes = all_routes;

  const isEdit = Boolean(id);

  const [mainCategories, setMainCategories] = useState<{ label: string; value: string }[]>([]);
  const [subActivities, setSubActivities] = useState<{ label: string; value: string }[]>([]);
  const [activities, setActivities] = useState<{ label: string; value: string }[]>([]);
  const [allProperties, setAllProperties] = useState<any[]>([]);
  const [showMileageModal, setShowMileageModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [remainingLogHours, setRemainingLogHours] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [mileageData, setMileageData] = useState<MileageEntry | null>(null);
  const [receiptImages, setReceiptImages] = useState<any[]>([]);
  const [receiptFiles, setReceiptFiles] = useState<File[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedImageUuid, setSelectedImageUuid] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState("");
  const [showActivityDescription, setShowActivityDescription] = useState(false);
  const [isTravelInvolved, setIsTravelInvolved] = useState(false);


  // Filtered property list
  const filteredProperties = allProperties.filter((p) =>
    `${p.name} ${p.address}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    getMainCategoryList();
    getAllPropertyList();
  }, [dispatch]);

  // get main category list
  const getMainCategoryList = async () => {
    try {
      const response = await mainCategoryList();
      const data = response?.data?.data || [];

      const mapped = data.map((cat: any) => ({
        label: cat.name,
        value: cat.uuid,
      }));

      setMainCategories(mapped);
      return
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  }

  // getPropertyListForFilter
  const getAllPropertyList = async () => {
    try {
      const response = await dispatch(getPropertyListForFilter({}));
      const allProps = response?.payload?.data || [];
      setAllProperties(allProps);
      return
    } catch (error) {
      console.error("Failed to fetch all property:", error);
    }
  }

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  //  Formik Setup
  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: true,  // ensure object-level tests run on change
    validateOnBlur: true,

    onSubmit: async (values) => {
      console.log("values ------- ", values);

      try {
        const formData = new FormData();

        Object.entries(values).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            if (typeof value === "boolean" || typeof value === "number") {
              formData.append(key, String(value));
            } else if (key === "mileageData" && value && typeof value === "object") {
              const mileage = value as MileageEntry;
              formData.append("source_name", mileage.startingLocation);
              formData.append("destination_name", mileage.destinationLocation);
              formData.append("route_trip", String(mileage.route_trip));
              formData.append("distance", mileage.miles);
              formData.append("is_recurring", String(mileage.isRecurring));
              formData.append("frequencies", mileage.days || "");
              formData.append("source_coordinates", mileage.source_coordinates || "");
              formData.append("destination_coordinates", mileage.destination_coordinates || "");
              mileage.photos?.forEach((file) => {
                formData.append("mileage_images", file);
              });
              if (isEdit && mileage.uuid) {
                formData.append("mileage_uuid", mileage.uuid);
              }
            } else if (key === "receipt_images" && Array.isArray(value)) {
              value.forEach((file: File) => formData.append("receipt_images", file));
            } else {
              formData.append(key, value as string);
            }
          }
        });

        if (receiptFiles?.length > 0) {
          receiptFiles.forEach((file) => {
            formData.append("receipt_images", file);
          });
        }

        console.log('values === ', values);

        if (isEdit) {
          await dispatch(updateLogHour({ uuid: id, values: formData }))
        } else {
          await dispatch(addLogHour(formData))
        }
        navigate(routes.spouseLogHours);
      } catch (err) {
        console.error(err);
        toast.error("Something went wrong!");
      }
    },
  });

  useEffect(() => {
    formik.setFieldValue("is_travel", isTravelInvolved);
    formik.setFieldValue("mileageData", mileageData);
  }, [isTravelInvolved, mileageData]);

  // Load log hour details if editing
  useEffect(() => {
    if (isEdit) {
      const fetchData = async () => {
        try {
          const response = await dispatch(getLogHourDetails({ uuid: id }));
          const logHourData = response?.payload?.data;
          console.log('logHourData == ', logHourData);

          if (logHourData) {
            const formattedDate = logHourData.date ? formatDateStringType(logHourData.date) : "";

            const data: any = {
              description: logHourData?.description || "",
              hours: parseInt(logHourData?.hours || "0", 10),
              minutes: parseInt(logHourData?.minutes || "0", 10),
              property: logHourData?.property?.uuid || "",
              date: formattedDate,
              main_category: logHourData?.main_category?.uuid || "",
              sub_activity: logHourData?.sub_activity?.uuid || "",
              activity: logHourData?.activity?.uuid || "",
              activity_description: logHourData?.activity_description || "",
              is_travel: logHourData?.is_travel || false,
              notes: logHourData?.notes || "",
              mileageData: logHourData?.mileage
                ? {
                  uuid: logHourData.mileage.uuid,
                  startingLocation: logHourData.mileage.source_name || "",
                  destinationLocation: logHourData.mileage.destination_name || "",
                  route_trip: logHourData.mileage.route_trip || false,
                  miles: String(logHourData.mileage.distance || ""),
                  isRecurring: logHourData.mileage.is_recurring || false,
                  days: logHourData.mileage.frequencies || "",
                  photos: [],
                  receipts: logHourData.mileage.receipts || [],
                }
                : null,
              receipt_images: null,
            };

            formik.setValues(data);
            setMileageData(data.mileageData);
            setReceiptImages(logHourData?.receipt_images || []);
            setIsTravelInvolved(!!logHourData?.is_travel);
          }

        } catch (error) {
          console.error("Failed to load log hour details:", error);
          toast.error("Failed to load log hour details");
        }
      };
      fetchData();
    }
  }, [id, isEdit, dispatch]);

  //  Fetch sub activities when category changes
  useEffect(() => {
    if (formik.values.main_category) {
      subActivityList(formik.values.main_category)
        .then((res) => {
          const mapped = res.data?.data?.map((sub: any) => ({
            label: sub.name,
            value: sub.uuid,
          })) || [];
          setSubActivities(mapped);
          if (!isEdit) {
            formik.setFieldValue("sub_activity", "");
            setActivities([]);
          }
        })
        .catch((err) => console.error("Failed sub activities:", err));
    }
  }, [formik.values.main_category, isEdit]);

  //  Fetch activities when sub activity changes
  useEffect(() => {
    if (formik.values.main_category && formik.values.sub_activity) {
      activityList(formik.values.main_category, formik.values.sub_activity)
        .then((res) => {
          const mapped = res.data?.data?.map((act: any) => ({
            label: act.name,
            value: act.uuid,
          })) || [];
          setActivities(mapped);
          if (!isEdit) {
            formik.setFieldValue("activity", "");
          }
        })
        .catch((err) => console.error("Failed activities:", err));
    }
  }, [formik.values.sub_activity, isEdit]);

  useEffect(() => {
    const selectedActivity = activities.find(act => act.value === formik.values.activity);
    setShowActivityDescription(selectedActivity ? selectedActivity?.label?.toLowerCase() === "others" : false);
  }, [formik.values.activity, activities]);

  // Handle delete confirmation
  const handleDeleteImage = () => {
    if (selectedImageUuid) {
      setReceiptImages((prev) => prev.filter((image) => image.uuid !== selectedImageUuid));
      setSelectedImageUuid(null);
      if (receiptFiles?.length + receiptImages?.length - 1 <= MAX_FILES) {
        setUploadError("");
      }
    }
  };

  // Handle opening delete modal
  const handleOpenDeleteModal = (uuid: string) => {
    setSelectedImageUuid(uuid);
    setShowDeleteModal(true);
  };

  // Handle closing delete modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedImageUuid(null);
  };

  // Handle successful deletion
  const handleDeleteSuccess = () => {
    handleDeleteImage();
    setShowDeleteModal(false);
  };

  // Function to fetch remaining log hours
  const fetchRemainingLogHours = async (date: string) => {
    try {
      if (!date) {
        setRemainingLogHours(null);
        return;
      }
      const response = await dispatch(getRemainingLogHour({ date }));
      setRemainingLogHours(response?.payload?.data?.message)

    } catch (error) {
      console.error("Failed to fetch remaining log hours:", error);
      setRemainingLogHours('Error fetching remaining log hours.');
    }
  };

  const validateAndAddFiles = (newFiles: File[]) => {
    // Filter out invalid files first
    const validFiles = newFiles.filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`File "${file.name}" exceeds 10MB limit.`);
        return false;
      }
      // if (!file.type.startsWith("image/") && !file.type.includes("pdf")) {
      //   toast.warn(`File "${file.name}" is not an image or PDF. Only images and PDFs are allowed.`);
      //   return false;
      // }
      return true;
    });

    if (validFiles.length === 0) return;

    const currentTotalSize = receiptFiles.reduce((acc, file) => acc + file.size, 0);
    const newTotalSize = validFiles.reduce((acc, file) => acc + file.size, 0);
    const futureTotalSize = currentTotalSize + newTotalSize;

    const totalFilesCount = receiptFiles.length + receiptImages.length + validFiles.length;

    // Check total file count
    if (totalFilesCount > MAX_FILES) {
      toast.error(`You can upload a maximum of ${MAX_FILES} files.`);
      return;
    }

    // Check total size limit
    if (futureTotalSize > MAX_TOTAL_SIZE) {
      toast.error("Total upload size cannot exceed 50MB.");
      return;
    }

    // All checks passed
    setReceiptFiles((prev) => [...prev, ...validFiles]);
    setUploadError("");
    toast.success(`${validFiles.length} file(s) added successfully.`);
  };


  return (
    <div className="layout">
      <Sidebar isOpen={isSidebarOpen} />

      <main className="content">
        <div className="profileSettings add-hours">
          <div className=" rounded add-hours-inner">
            <div className="add-properties-heading d-flex align-items-center gap-3 mb-4">
              <ClockYellowIcon />
              <h2 className="mb-0">Log Hours</h2>
            </div>

            <form onSubmit={formik.handleSubmit} encType="multipart/form-data">
              {/* Task Description */}
              <div className="mb-4">
                <label htmlFor="description" className="form-label">Task Description</label>
                <textarea
                  id="description"
                  name="description"
                  className="form-control commonInput bg-light"
                  rows={4}
                  maxLength={500}
                  placeholder="Enter Task Description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                />
                <div className="text-end text-muted small mt-1">
                  {formik.values.description.length}/500 Characters
                </div>
                {formik.touched.description && formik.errors.description && (
                  <div className="text-danger">{formik.errors.description}</div>
                )}
              </div>

              {/* Duration and Property */}
              <div className="row g-3 mb-3">
                <div className="col-12 col-sm-12 col-md-12 col-lg-3">
                  <label className="form-label">Duration Hours</label>
                  <input
                    type="number"
                    name="hours"
                    className="form-control commonInput bg-light"
                    min={0}
                    max={24}
                    value={formik.values.hours}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.hours && formik.errors.hours && (
                    <div className="text-danger">{formik.errors.hours}</div>
                  )}
                </div>

                <div className="col-12 col-sm-12 col-md-12 col-lg-3">
                  <label className="form-label d-none d-md-block">Duration Minutes</label>
                  <input
                    type="number"
                    name="minutes"
                    className="form-control commonInput bg-light"
                    min={0}
                    max={60}
                    value={formik.values.minutes}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.minutes && formik.errors.minutes && (
                    <div className="text-danger">{formik.errors.minutes}</div>
                  )}
                </div>

                {/* Property Dropdown */}
                <div
                  className="col-12 col-sm-12 col-md-12 col-lg-6"
                  ref={dropdownRef}
                >
                  <label className="form-label">Property</label>
                  <div className="dropdown ">
                    <button
                      type="button"
                      className="form-select d-flex justify-content-between align-items-center bg-light form-control commonInput "
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    >
                      {allProperties?.find(p => p?.uuid === formik?.values?.property)?.name || "Select Property"}
                      <i
                        className={`bi bi-chevron-${isDropdownOpen ? "up" : "down"}`}
                      />
                    </button>

                    {isDropdownOpen && (
                      <div
                        className="dropdown-menu proprty-dropdown show w-100 p-3 shadow-sm mt-1 bg-light"
                        style={{ maxHeight: "initial" }}
                      >
                        <div className="input-group mb-3 commonInput bg-light bg-white">
                          <span className="input-group-text bg-white border-end-0 ">
                            <SearchIcon />
                          </span>
                          <input
                            type="text"
                            placeholder="Search properties by name or address"
                            className="name form-control border-start-0"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e?.target?.value)}
                          />
                        </div>

                        {filteredProperties?.length > 0 ? (
                          filteredProperties?.map((property) => (
                            <div
                              key={property?.uuid}
                              className="property-drop form-check d-flex align-items-left mb-3 justify-content-between form-control commonInput  bg-light"
                              onClick={() => {
                                formik.setFieldValue("property", property.uuid);
                                setIsDropdownOpen(false);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <label className="form-check-label ms-2">
                                <strong>{property?.name}</strong>
                                <small className="text-muted">
                                  {property?.address}, {property?.city}, {property?.state}{" "}  {property?.zipcode}
                                </small>
                              </label>
                              <input
                                type="radio"
                                className="form-check-input mt-1"
                                name="propertyRadio"
                                checked={formik?.values?.property === property?.uuid}
                                readOnly
                              />
                            </div>
                          ))
                        ) : (
                          <div className="text-muted text-center">
                            No results found.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {formik.touched.property && formik.errors.property && (
                    <div className="text-danger">{formik.errors.property}</div>
                  )}
                </div>
              </div>

              {/* Display Remaining Log Hours */}
              {remainingLogHours && (
                <div className="mb-3">
                  <p className="text-muted">{remainingLogHours}</p>
                </div>
              )}

              {/* Date, Category, Sub Activity */}
              <div className="row g-3 mb-3">
                <div className="col-12 col-sm-12 col-md-12 col-lg-6">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    name="date"
                    className="form-control commonInput bg-light"
                    value={formik.values.date}
                    // onChange={formik.handleChange}
                    max={new Date().toISOString().split("T")[0]} // Set max to today's date
                    onChange={(e) => {
                      formik.handleChange(e); // Update formik value
                      // Fetch remaining log hours if property is selected
                      if (e.target.value) {
                        fetchRemainingLogHours(e.target.value);
                      } else {
                        setRemainingLogHours(null); // Clear message if no property or date
                      }
                    }}
                  />
                  {formik.touched.date && formik.errors.date && (
                    <div className="text-danger">{formik.errors.date}</div>
                  )}
                </div>

                <div className="col-12 col-sm-12 col-md-12 col-lg-6">
                  <label className="form-label">Activity Category</label>
                  <select
                    name="main_category"
                    className="form-select form-control commonInput  bg-light"
                    value={formik.values.main_category}
                    onChange={formik.handleChange}
                  >
                    <option className="first" value="" disabled selected hidden>
                      Choose Activity Category
                    </option>
                    {mainCategories.map((cat) => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                  {formik.touched.main_category && formik.errors.main_category && (
                    <div className="text-danger">{formik.errors.main_category}</div>
                  )}
                </div>
              </div>

              {/* Sub Activity, Activity */}
              <div className="row g-3 mb-3">
                <div className="col-12 col-sm-12 col-md-12 col-lg-6">
                  <label className="form-label">Sub Activity</label>
                  <select
                    name="sub_activity"
                    className="form-select form-control commonInput bg-light"
                    value={formik?.values?.sub_activity}
                    onChange={formik.handleChange}
                  >
                    <option className="first" value="" disabled selected hidden>
                      Choose a Sub Activity
                    </option>
                    {subActivities.map((sub) => (
                      <option key={sub?.value} value={sub?.value}>{sub?.label}</option>
                    ))}
                  </select>
                  {formik.touched?.sub_activity && formik.errors?.sub_activity && (
                    <div className="text-danger">{formik.errors?.sub_activity}</div>
                  )}
                </div>

                <div className="col-12 col-sm-12 col-md-12 col-lg-6">
                  <label className="form-label">Activity </label>
                  <select
                    name="activity"
                    className="form-select form-control commonInput bg-light"
                    value={formik.values.activity}
                    onChange={formik.handleChange}
                  >
                    <option className="first" value="" disabled selected hidden>
                      Choose Activity
                    </option>
                    {activities.map((act) => (
                      <option key={act.value} value={act.value}>{act.label}</option>
                    ))}
                  </select>
                  {formik.touched.activity && formik.errors.activity && (
                    <div className="text-danger">{formik.errors.activity}</div>
                  )}
                </div>
              </div>

              {/* Add Description Section (conditionally rendered) */}
              {showActivityDescription && (
                <div className="mb-4">
                  <label htmlFor="activity_description" className="form-label">Add Description</label>
                  <textarea
                    id="activity_description"
                    name="activity_description"
                    className="form-control commonInput bg-light"
                    rows={4}
                    maxLength={500}
                    placeholder="Enter Additional Description"
                    value={formik.values.activity_description || ""}
                    onChange={formik.handleChange}
                  />
                  <div className="text-end text-muted small mt-1">
                    {formik.values.activity_description.length}/500 Characters
                  </div>
                  {formik.touched.activity_description && formik.errors.activity_description && (
                    <div className="text-danger">{formik.errors.activity_description}</div>
                  )}
                </div>
              )}

              {/* Travel Checkbox */}
              <div className="activity-involve-travel-area bg-light mb-4 form-control commonInput flex-wrap">
                <div className="activity-involve-travel-inner form-check form-switch-1 bg-warning-subtle px-4 py-2 rounded ">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="travelCheckbox"
                    name="is_travel"
                    checked={isTravelInvolved}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsTravelInvolved(checked);
                      if (!checked) formik.setFieldValue("mileageData", null);
                    }}
                    onBlur={formik.handleBlur}
                  />
                  <TravelActivityIcon />
                  <label
                    className="form-check-label ms-2"
                    htmlFor="travelCheckbox">
                    Did this activity involve travel?
                  </label>
                  {isTravelInvolved && (
                    <div className=" Mileage-Entry">
                      <button
                        type="button"
                        className="btn btn-warning btn-sm"
                        onClick={() => setShowMileageModal(true)}
                      >
                        Quick Link to Mileage Entry
                      </button>
                    </div>
                  )}
                </div>
                {formik.touched.mileageData && formik.errors.mileageData && (
                  <div className="text-danger mt-2">{formik.errors.mileageData}</div>
                )}
              </div>

              {/* Enhanced Information */}
              <div className="border rounded p-3 mb-4 button bg-light Enhanced-Information-Section">
                <div className="d-flex justify-content-between align-items-center mb-3 logged-button px-3 px-md-0 flex-wrap">
                  <div className="enhanced-information-heading d-flex justify-content-center align-items-center gap-2 flex-wrap mb-3">
                    <GreenFileIcon />
                    <h3>Enhanced Information</h3>
                  </div>
                </div>

                {/* Upload Receipt */}
                <div className="mb-3 upload-section">
                  <label className="form-label d-flex justify-content-left align-items-center gap-2">
                    <UploadIcon />
                    Upload Supporting Documents
                  </label>

                  <div
                    className="upload-area rounded text-center p-3 bg-white"
                    style={{
                      borderStyle: "dashed",
                      borderWidth: "2px",
                      borderColor: "#dee2e6",
                      cursor: "pointer",
                    }}
                    onClick={() => document.getElementById("receiptUploadInput")?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      // if (e.dataTransfer.files) {
                      //   const newFiles = Array.from(e.dataTransfer.files);
                      //   if (receiptFiles.length + receiptImages.length + newFiles.length > MAX_FILES) {
                      //     setUploadError(`You can only upload up to ${MAX_FILES} files.`);
                      //     return;
                      //   }
                      //   setReceiptFiles((prev) => [...prev, ...newFiles]);
                      //   setUploadError("");
                      // }
                      if (e.dataTransfer.files) {
                        const newFiles = Array.from(e.dataTransfer.files);
                        validateAndAddFiles(newFiles);
                      }
                    }}
                  >
                    <FileUploadWhiteIcon />
                    <p className="mb-0 text-muted">
                      Click or drag file to this area to upload
                    </p>

                    <input
                      type="file"
                      id="receiptUploadInput"
                      hidden
                      multiple
                      // accept="image/*"
                      // onChange={(e) => {
                      //   if (e.target.files?.length) {
                      //     const newFiles = Array.from(e.target.files);
                      //     if (receiptFiles.length + receiptImages.length + newFiles.length > MAX_FILES) {
                      //       setUploadError(`You can only upload up to ${MAX_FILES} files.`);
                      //       return;
                      //     }
                      //     setReceiptFiles((prev) => [...prev, ...newFiles]);
                      //     setUploadError("");
                      //   }
                      // }}
                      onChange={(e) => {
                        if (e.target.files?.length) {
                          const newFiles = Array.from(e.target.files);
                          validateAndAddFiles(newFiles);
                          e.target.value = ""; // Reset input
                        }
                      }}
                    />
                  </div>

                  {/* Inline error */}
                  {uploadError && (
                    <div className="text-danger mt-2">{uploadError}</div>
                  )}

                  {/* Show uploaded files and API receipt images */}
                  {(receiptFiles?.length > 0 || receiptImages?.length > 0) && (
                    <div className="mt-3 uploaded-files-section">

                      <div className="mb-3 p-2 bg-light rounded border">
                        <small className="text-muted fw-semibold">
                          Uploaded: {receiptFiles.length + receiptImages.length} / {MAX_FILES} files |
                          Size: {(receiptFiles.reduce((acc, f) => acc + f.size, 0) / (1024 * 1024)).toFixed(2)} MB / 50 MB
                        </small>
                        {receiptFiles.reduce((acc, f) => acc + f.size, 0) > MAX_TOTAL_SIZE * 0.9 && (
                          <span className="text-warning ms-2">Warning: Near limit!</span>
                        )}
                      </div>

                      <div className="mt-2 text-start">
                        {receiptImages.map((image, idx) => (
                          <div key={image?.uuid} className="d-flex justify-content-between align-items-center mb-1">
                            <a href={image?.image} target="_blank" rel="noopener noreferrer">
                              {image?.image.split("/").pop()}
                            </a>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleOpenDeleteModal(image.uuid)}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        ))}

                        {receiptFiles.map((file, idx) => (
                          <div key={idx} className="d-flex justify-content-between align-items-center mb-1">
                            <span>{file?.name}</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => {
                                setReceiptFiles((prev) => {
                                  const updated = prev.filter((_, i) => i !== idx);
                                  if (updated.length + receiptImages.length <= MAX_FILES) {
                                    setUploadError("");
                                  }
                                  return updated;
                                });
                              }}
                            >
                              <i className="fas fa-times cross-btn"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Additional Notes */}
                <div className="mb-4 ">
                  <label className="form-label">Additional Notes</label>
                  <textarea
                    name="notes"
                    className="form-control commonInput  bg-white"
                    rows={4}
                    maxLength={500}
                    placeholder="Add Additional Notes"
                    value={formik?.values?.notes}
                    onChange={formik?.handleChange}
                  />
                  <div className="text-end text-muted small mt-1">
                    {formik.values.notes.length}/500 Characters
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="text-end">
                <button
                  type="submit"
                  className="log-time-button btn primaryButton  btn-primary d-inline-flex justify-content-center align-items-center  btn-block"
                  disabled={!formik.isValid || formik.isSubmitting}
                >
                  {isEdit ? "Update Log Time" : "Submit"}
                </button>
              </div>

            </form>
          </div>
        </div>
      </main>

      <MileageEntryModal
        show={showMileageModal}
        onHide={() => setShowMileageModal(false)}
        data={mileageData}
        onSave={(entry: MileageEntry) => {
          if (!entry.startingLocation || !entry.destinationLocation || !entry.miles) {
            toast.error("Please fill all required mileage fields");
            return;
          }
          setMileageData(entry);
          formik.setFieldValue("mileageData", entry, true);
          formik.setFieldTouched("mileageData", true);
          setShowMileageModal(false);
        }}
      />

      <DeleteModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        type="deleteLogHourReceipt"
        uuid={selectedImageUuid}
        data={id}
        onSuccess={handleDeleteSuccess}
      />

    </div>
  );
};

export default SpouseLogHoursAdd;
